import React, { useState, useRef } from "react";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteParams } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { T } from "services/lang/LanguageService";
import { InputSwitch } from "primereact/inputswitch";
import {
  DatasetStatusMonitoringProps,
  StatusMonitoringIndicator,
  Visuals,
} from "../charts/interfaces";

interface IDatasetStatusMonitoringEditor {
  config: DatasetStatusMonitoringProps;
  onConfigChange: (newConfig: DatasetStatusMonitoringProps) => void;
}

const defaultIndicator = {
  number: 0,
  visual: Visuals.NONE,
  color: "#000000",
  title: "",
  icon: "",
  sound: false,
};

export enum AvailableIcons {
  BELL = "pi pi-bell",
  VERIFIED = "pi pi-verified",
  TIMES = "pi pi-times",
  EXCLAMATION_CIRCLE = "pi pi-exclamation-circle",
  CHECK = "pi pi-check",
  CHECK_CIRCLE = "pi pi-check-circle",
  EYE = "pi pi-eye",
  ARROW_UP = "pi pi-arrow-up",
  ARROW_DOWN = "pi pi-arrow-down",
  ARROW_LEFT = "pi pi-arrow-left",
  ARROW_RIGHT = "pi pi-arrow-right",
  ARROW_CIRCLE_UP = "pi pi-arrow-circle-up",
  ARROW_CIRCLE_DOWN = "pi pi-arrow-circle-down",
  ARROW_CIRCLE_LEFT = "pi pi-arrow-circle-left",
  ARROW_CIRCLE_RIGHT = "pi pi-arrow-circle-right",
  TRASH = "pi pi-trash",
  PLUS = "pi pi-plus",
  MINUS = "pi pi-minus",
  PLUS_CIRCLE = "pi pi-plus-circle",
  MINUS_CIRCLE = "pi pi-minus-circle",
}

const DatasetStatusMonitoringEditor = (props: IDatasetStatusMonitoringEditor) => {
  const [currentConfig, setCurrentConfig] = useState<DatasetStatusMonitoringProps>({
    ...props.config,
  });
  const [currentIndicators, setCurrentIndicators] = useState<StatusMonitoringIndicator[]>(
    currentConfig.indicators || []
  );
  const [indicatorsDialogVisible, setIndicatorsDialogVisible] = useState<boolean>(false);
  const toast = useRef<Toast>(null);

  const handleColorEditorCallback = (options: ColumnEditorOptions, newField: string) => {
    const indicators = [...currentIndicators];
    if (options.rowIndex !== -1) {
      indicators[options.rowIndex] = { ...indicators[options.rowIndex], ...{ color: newField } };
      setCurrentIndicators(indicators);
      options.editorCallback?.(indicators[options.rowIndex].color);
    }
  };

  const lineColorEditor = (options: ColumnEditorOptions) => {
    const value =
      typeof options.rowData.color === "string" && options.rowData.color.length === 7
        ? options.rowData.color
        : "#000000";

    return (
      <div className="flex gap-1 align-items-center">
        <ColorPicker
          value={value}
          onValueChange={e => handleColorEditorCallback(options, e.target.value)}
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() => handleColorEditorCallback(options, "#000000")}
        />
      </div>
    );
  };

  const colorBodyTemplate = (rowData: StatusMonitoringIndicator) => {
    return (
      <div
        style={{
          width: "25px",
          height: "25px",
          backgroundColor: rowData.color,
          borderRadius: "5px",
          border: "1px solid #c8c8c8",
        }}
      />
    );
  };

  const handleNumberEditor = (options: ColumnEditorOptions) => {
    return (
      <InputNumber
        value={options.value}
        onChange={e => {
          const newValue = e.value;
          const isDuplicate = currentIndicators.some(
            (ind: StatusMonitoringIndicator) => ind.number === newValue && ind !== options.rowData
          );

          if (isDuplicate) {
            toast.current?.show({
              severity: "error",
              summary: T("error.validation-error"),
              detail: T("error.duplicate-number-not-allowed"),
              life: 3000,
            });
          } else {
            options.editorCallback?.(newValue);
          }
        }}
        inputStyle={{ width: "100%" }}
      />
    );
  };

  const handleTitleEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const handleVisualsEditor = (options: ColumnEditorOptions) => {
    const visualOptions = Object.keys(Visuals).map(key => ({
      label: Visuals[key as keyof typeof Visuals],
      value: Visuals[key as keyof typeof Visuals],
    }));

    return (
      <Dropdown
        value={options.value}
        options={visualOptions}
        onChange={e => options.editorCallback?.(e.value)}
      />
    );
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteParams) => {
    const indicators = [...currentIndicators];
    const { newData, index } = e;
    indicators[index] = newData;
    setCurrentIndicators(indicators);
  };

  const handleAddNewIndicator = () => {
    const existingNumbers = currentIndicators.map(indicator => indicator.number);
    let newNumber = 0;
    while (existingNumbers.includes(newNumber)) {
      newNumber++;
    }

    const newIndicator = { ...defaultIndicator, number: newNumber };
    setCurrentIndicators([...currentIndicators, newIndicator]);
  };

  const handleConfigChange = (newConfig: DatasetStatusMonitoringProps) => {
    setCurrentConfig(newConfig);
    props.onConfigChange(newConfig);
  };

  const handleSaveIndicators = () => {
    handleConfigChange({ ...currentConfig, indicators: currentIndicators });
    setIndicatorsDialogVisible(false);
  };

  const iconBodyTemplate = (rowData: StatusMonitoringIndicator) => {
    return <i className={rowData.icon} />;
  };

  const handleIconEditor = (options: ColumnEditorOptions) => {
    const iconsOptions = Object.keys(AvailableIcons).map(key => ({
      label: AvailableIcons[key as keyof typeof AvailableIcons],
      value: AvailableIcons[key as keyof typeof AvailableIcons],
    }));

    const itemTemplate = (option: ColumnEditorOptions) => {
      return <i className={option.value} style={{ marginRight: "0.5rem" }} />;
    };

    const valueTemplate = (option: ColumnEditorOptions) => {
      return option ? <i className={option.value} /> : <span>{T("action.select-an-icon")}</span>;
    };

    return (
      <Dropdown
        value={options.value}
        options={iconsOptions}
        onChange={e => options.editorCallback?.(e.value)}
        itemTemplate={itemTemplate}
        valueTemplate={valueTemplate}
      />
    );
  };

  const soundBodyTemplate = (rowData: StatusMonitoringIndicator) => {
    return <InputSwitch disabled checked={rowData.sound} />;
  };

  const handleSelectSoundOption = (options: ColumnEditorOptions) => {
    const { rowData } = options;

    return (
      <InputSwitch
        checked={rowData.sound}
        onChange={e => {
          const updatedIndicators = [...currentIndicators];
          const index = updatedIndicators.findIndex(ind => ind.number === rowData.number);
          if (index !== -1) {
            updatedIndicators[index] = {
              ...updatedIndicators[index],
              sound: e.value,
            };
            setCurrentIndicators(updatedIndicators);
            options.editorCallback?.(updatedIndicators[index].sound);
          }
        }}
      />
    );
  };

  const indicatorsDialogFooterTemplate = () => {
    return (
      <div className="flex justify-content-between">
        <Button
          label={T("word.add")}
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleAddNewIndicator}
        />
        <div>
          <Button
            label={T("action.save")}
            icon="pi pi-check"
            className="p-button-success"
            onClick={handleSaveIndicators}
          />
          <Button
            label={T("action.close")}
            icon="pi pi-times"
            className="p-button-danger"
            onClick={() => setIndicatorsDialogVisible(false)}
          />
        </div>
      </div>
    );
  };

  const deleteButtonTemplate = (rowData: StatusMonitoringIndicator) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-danger"
        onClick={() => handleDeleteRow(rowData)}
      />
    );
  };

  const handleDeleteRow = (rowData: StatusMonitoringIndicator) => {
    const updatedIndicators = [...currentIndicators];
    const index = updatedIndicators.findIndex(ind => ind.number === rowData.number);
    if (index !== -1) {
      updatedIndicators.splice(index, 1);
      setCurrentIndicators(updatedIndicators);
    }
  };

  return (
    <div className="flex flex-column">
      <Toast ref={toast} />
      <div className="flex flex-column gap-1">
        <label htmlFor="identifier">{T("word.identifier")}</label>
        <DatasetDropdown
          value={currentConfig.identifiers?.[0] as string | null | undefined}
          onChange={e => {
            const selectedIdentifier = e ?? "";
            const updatedIdentifiers = [
              selectedIdentifier,
              ...(currentConfig.identifiers || []).filter(id => id !== selectedIdentifier),
            ];
            handleConfigChange({ ...currentConfig, identifiers: updatedIdentifiers });
          }}
        />
      </div>

      <div>
        <Button
          label={T("action.set-indicators")}
          type="button"
          onClick={() => setIndicatorsDialogVisible(true)}
          icon="pi pi-plus"
          className="w-max mt-3"
        />
      </div>
      <div className="flex flex-row mb-3">
        <div className="col-3 flex flex-column justify-content-between mb-3">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              value={currentConfig.updateFrequency as number}
              onValueChange={e =>
                handleConfigChange({ ...currentConfig, updateFrequency: e.value as number })
              }
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        <div className="col-3 flex flex-column justify-content-between mb-3">
          <label htmlFor="indicators">
            {T("label.dataset-status-monitoring.size-scale-from-1-to-2")}
          </label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="scale"
              value={currentConfig.scale as number}
              onValueChange={e =>
                handleConfigChange({ ...currentConfig, scale: e.value as number })
              }
              mode="decimal"
              maxFractionDigits={2}
              max={2}
              min={0}
            />
          </div>
        </div>
        <div className="col-3 flex flex-column justify-content-between mb-3">
          <label htmlFor="updateCheckTime">
            {T("label.dataset-status-monitoring.update-check-time")} ({T("time.seconds")})
          </label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateCheckTime"
              value={currentConfig.updateCheckTime as number}
              onValueChange={e =>
                handleConfigChange({ ...currentConfig, updateCheckTime: e.value as number })
              }
            />
          </div>
        </div>
      </div>

      {indicatorsDialogVisible && (
        <Dialog
          header={T("word.indicators")}
          visible={indicatorsDialogVisible}
          style={{ width: "50vw" }}
          footer={indicatorsDialogFooterTemplate()}
          onHide={() => setIndicatorsDialogVisible(false)}
        >
          <DataTable value={currentIndicators} onRowEditComplete={onRowEditComplete} editMode="row">
            <Column
              field="number"
              header={T("word.dataset-value")}
              style={{ width: "10%" }}
              editor={handleNumberEditor}
            />
            <Column
              field="icon"
              header={T("word.icon")}
              style={{ width: "10%" }}
              body={iconBodyTemplate}
              editor={handleIconEditor}
            />
            <Column
              field="title"
              header={T("word.display-text")}
              style={{ width: "45%" }}
              editor={handleTitleEditor}
            />
            <Column
              field="color"
              header={T("word.icon-color")}
              style={{ width: "20%" }}
              body={colorBodyTemplate}
              editor={lineColorEditor}
            />
            <Column
              field="visual"
              header={T("word.text-effect")}
              style={{ width: "10%" }}
              editor={handleVisualsEditor}
            />
            <Column
              field="sound"
              header={T("word.sound")}
              style={{ width: "5%" }}
              body={soundBodyTemplate}
              editor={handleSelectSoundOption}
            />
            <Column rowEditor style={{ width: "5%" }} />
            <Column field="delete" body={deleteButtonTemplate} style={{ width: "5%" }} />
          </DataTable>
        </Dialog>
      )}
    </div>
  );
};

export default DatasetStatusMonitoringEditor;
